import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { crmService } from '../services/crmService'

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : 'Never')
const label = (s) => (s ? String(s).replace(/_/g, ' ') : '-')

export default function CrmOrganisationDetail() {
    const { id } = useParams()
    const [state, setState] = useState({ org: null, loading: true, error: null })

    useEffect(() => {
        let cancelled = false
        setState({ org: null, loading: true, error: null })
        crmService.getOrganisation(id)
            .then((res) => {
                if (cancelled) return
                if (!res.success) throw new Error('Failed to load organisation')
                setState({ org: res.data, loading: false, error: null })
            })
            .catch((err) => { if (!cancelled) setState({ org: null, loading: false, error: err.message || 'Failed to load organisation' }) })
        return () => { cancelled = true }
    }, [id])

    const { org, loading, error } = state
    const [flagBusy, setFlagBusy] = useState(false)
    const [flagError, setFlagError] = useState(null)

    const toggleInternal = async () => {
        const next = !org.isInternal
        const msg = next
            ? `Mark "${org.name}" as an internal / test account? It will be excluded from customer and commercial metrics (paying organisations, MRR/ARR, users, logins, usage, conversion).`
            : `Treat "${org.name}" as a customer account again? It will be included in customer and commercial metrics.`
        if (!window.confirm(msg)) return
        setFlagBusy(true)
        setFlagError(null)
        try {
            await crmService.setInternal(org.id, next)
            setState((s) => ({ ...s, org: { ...s.org, isInternal: next } }))
        } catch (err) {
            setFlagError(err.message || 'Could not update the account type')
        } finally {
            setFlagBusy(false)
        }
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <Link to="/crm" className="text-sm font-bold text-brand-navy hover:underline">← Back to CRM</Link>

            {loading && <p className="text-sm text-admin-muted">Loading organisation...</p>}
            {error && <div role="alert" className="p-3 text-sm rounded-md bg-red-50 text-status-error border border-red-200">{error}</div>}

            {org && (
                <>
                    <div>
                        <h1 className="text-2xl font-bold text-admin-heading tracking-tight">
                            {org.name}
                            {org.isInternal && <span className="ml-3 badge badge-warning align-middle">Internal / test</span>}
                        </h1>
                        <p className="text-sm text-admin-muted mt-1">
                            {org.primaryContact?.name || 'No primary contact'}{org.primaryContact?.email ? ` · ${org.primaryContact.email}` : ''}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card title="Registration and account">
                            <Row k="Registered" v={fmtDate(org.registeredAt)} />
                            <Row k="Account status" v={<span className="badge badge-neutral">{label(org.accountStatus)}</span>} />
                            <Row k="Suspended" v={org.lifecycle?.suspended ? 'Yes' : 'No'} />
                            <Row k="Historically reactivated" v={org.lifecycle?.reactivated ? 'Yes' : 'No'} />
                            <Row k="Deletion requested" v={org.lifecycle?.deletionRequested ? 'Yes' : 'No'} />
                            <Row k="Account type" v={
                                <span className="flex items-center gap-3">
                                    <span className={`badge ${org.isInternal ? 'badge-warning' : 'badge-neutral'}`}>{org.isInternal ? 'Internal / test' : 'Customer'}</span>
                                    <button type="button" onClick={toggleInternal} disabled={flagBusy} className="btn-secondary btn-xs">
                                        {flagBusy ? 'Saving…' : org.isInternal ? 'Treat as customer' : 'Mark as internal / test'}
                                    </button>
                                </span>
                            } />
                            {flagError && <p className="text-xs text-status-error mt-1">{flagError}</p>}
                        </Card>

                        <Card title="Usage">
                            <Row k="Last login" v={fmtDate(org.lastLogin)} />
                            {Object.entries(org.usage || {}).map(([k, v]) => (
                                <Row key={k} k={k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())} v={String(v)} />
                            ))}
                        </Card>

                        <Card title="Opportunity counts">
                            <Row k="Profile Matches" v={org.counts?.profileMatches ?? '-'} />
                            <Row k="Wider Discovery" v={org.counts?.widerDiscovery ?? '-'} />
                            <Row k="Saved" v={org.counts?.saved ?? '-'} />
                        </Card>

                        <Card title="Tender Settings">
                            <Row k="Status" v={<span className={`badge ${org.tenderSettings?.complete ? 'badge-success' : 'badge-warning'}`}>{org.tenderSettings?.complete ? 'Complete' : 'Incomplete'}</span>} />
                            {Object.entries(org.tenderSettings || {}).filter(([k]) => k !== 'complete').map(([k, v]) => (
                                <Row key={k} k={k.charAt(0).toUpperCase() + k.slice(1)} v={Array.isArray(v) ? (v.join(', ') || '-') : String(v)} />
                            ))}
                        </Card>
                    </div>

                    <Card title="Trial and paid subscription history">
                        {(org.subscriptionHistory || []).length === 0
                            ? <p className="text-sm text-admin-muted">No subscription history.</p>
                            : (
                                <ul className="divide-y divide-gray-100">
                                    {org.subscriptionHistory.map((h, i) => (
                                        <li key={i} className="py-2 flex gap-4 text-sm">
                                            <span className="text-admin-muted w-28 flex-shrink-0">{fmtDate(h.date)}</span>
                                            <span className="text-admin-text">{h.event}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                    </Card>

                    <Card title="Associated users">
                        {(org.users || []).length === 0
                            ? <p className="text-sm text-admin-muted">No associated users.</p>
                            : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-left text-sm">
                                        <thead className="border-b border-admin-border text-admin-muted">
                                            <tr>{['Name', 'Email', 'Role', 'Status'].map((h) => <th key={h} scope="col" className="py-2 pr-4 font-bold uppercase text-xs tracking-wider">{h}</th>)}</tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {org.users.map((u) => (
                                                <tr key={u.id}>
                                                    <td className="py-2 pr-4 font-bold text-brand-navy">{u.name}</td>
                                                    <td className="py-2 pr-4">{u.email}</td>
                                                    <td className="py-2 pr-4">{label(u.role)}</td>
                                                    <td className="py-2 pr-4">{label(u.status)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                    </Card>

                    {(org.linkedContacts || []).length > 0 && (
                        <Card title="Linked Insights contacts">
                            <ul className="divide-y divide-gray-100 text-sm">
                                {org.linkedContacts.map((c) => (
                                    <li key={c.id} className="py-2">
                                        <span className="font-bold text-brand-navy">{c.name}</span> · {c.email}
                                        <span className="block text-xs text-admin-muted">Source: {c.source || '-'} · Consent: {c.consent || '-'}</span>
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    )}
                </>
            )}
        </div>
    )
}

function Card({ title, children }) {
    return (
        <section className="card-admin p-5" aria-label={title}>
            <h2 className="text-sm font-bold text-admin-muted uppercase tracking-wider mb-3">{title}</h2>
            {children}
        </section>
    )
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-gray-50 last:border-0">
            <span className="text-admin-muted">{k}</span>
            <span className="text-admin-text font-semibold text-right">{v}</span>
        </div>
    )
}
